import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Upload, Trash2, Check, Palette, X, Loader2 } from 'lucide-react';
import { designService, ThemeResponse } from '../services/designService';

interface DesignUploadPageProps {
  navigateTo: (page: string) => void;
}

const DesignUploadPage: React.FC<DesignUploadPageProps> = ({ navigateTo }) => {
  const [themes, setThemes] = useState<ThemeResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const loadThemes = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await designService.listThemes();
      setThemes(data);
    } catch (error) {
      console.error('Failed to load themes:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadThemes();
  }, [loadThemes]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowed = [
      'image/png',
      'image/jpeg',
      'image/webp',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ];
    // Also accept by extension for browsers that report empty/generic MIME for .pptx
    const isPptx = file.name?.toLowerCase().endsWith('.pptx');
    if (!allowed.includes(file.type) && !isPptx) {
      setUploadError('PNG, JPEG, WebP画像またはPPTXファイルのみアップロード可能です。');
      return;
    }

    // Validate size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      setUploadError('ファイルサイズは50MB以下にしてください。');
      return;
    }

    setUploadError(null);
    setUploadSuccess(null);
    setIsUploading(true);

    try {
      const result = await designService.uploadDesign(file);
      setUploadSuccess(
        'テーマ「' + result.theme_name + '」を作成し、有効化しました。次回のスライド生成から反映されます。'
      );
      await loadThemes();
    } catch (error: any) {
      setUploadError(error.message || 'アップロードに失敗しました。');
    } finally {
      setIsUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleActivate = async (themeId: string) => {
    try {
      await designService.activateTheme(themeId);
      await loadThemes();
    } catch (error) {
      console.error('Failed to activate theme:', error);
    }
  };

  const handleDeactivate = async () => {
    try {
      await designService.deactivateTheme();
      await loadThemes();
    } catch (error) {
      console.error('Failed to deactivate theme:', error);
    }
  };

  const handleDelete = async (themeId: string) => {
    try {
      await designService.deleteTheme(themeId);
      setDeleteConfirm(null);
      await loadThemes();
    } catch (error) {
      console.error('Failed to delete theme:', error);
    }
  };

  const activeTheme = themes.find((t) => t.is_active);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigateTo('settings')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            Slide Design
          </h1>
          <p className="text-xs text-gray-500">
            カスタムデザインテーマの管理
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Upload Section */}
        <div className="bg-white rounded-lg border p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">
            デザインをアップロード
          </h2>
          <p className="text-xs text-gray-500 mb-3">
            スライドデザインの画像またはPPTXテンプレートをアップロードすると、色・フォント・レイアウトを分析してカスタムテーマを作成します。
          </p>

          <label className="block">
            <div
              className={
                'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ' +
                (isUploading
                  ? 'border-blue-300 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50')
              }
            >
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 size={24} className="text-blue-500 animate-spin" />
                  <span className="text-sm text-blue-600">
                    デザインを分析中...
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload size={24} className="text-gray-400" />
                  <span className="text-sm text-gray-600">
                    PNG / JPEG / WebP / PPTX をドロップまたはクリック
                  </span>
                  <span className="text-xs text-gray-400">最大50MB</span>
                </div>
              )}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,.pptx"
                className="hidden"
                onChange={handleFileUpload}
                disabled={isUploading}
              />
            </div>
          </label>

          {uploadError && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <X size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700">{uploadError}</p>
            </div>
          )}

          {uploadSuccess && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
              <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-700">{uploadSuccess}</p>
            </div>
          )}
        </div>

        {/* Active Theme */}
        <div className="bg-white rounded-lg border p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">
            現在のテーマ
          </h2>
          {activeTheme ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex gap-1">
                  {['primary', 'secondary', 'accent'].map((key) => (
                    <div
                      key={key}
                      className="w-6 h-6 rounded border border-gray-200"
                      style={{
                        backgroundColor:
                          '#' + (activeTheme.colors[key] || 'CCCCCC'),
                      }}
                      title={key}
                    />
                  ))}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {activeTheme.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {activeTheme.fonts.heading} / {activeTheme.fonts.body}
                  </p>
                </div>
              </div>
              <button
                onClick={handleDeactivate}
                className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded px-2 py-1"
              >
                デフォルトに戻す
              </button>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              デフォルトのAdvisioテーマを使用中
            </p>
          )}
        </div>

        {/* Theme List */}
        <div className="bg-white rounded-lg border p-4">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">
            カスタムテーマ一覧
          </h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="text-gray-400 animate-spin" />
            </div>
          ) : themes.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">
              カスタムテーマはまだありません。
              <br />
              上のアップロードからデザインを追加してください。
            </p>
          ) : (
            <div className="space-y-2">
              {themes.map((theme) => (
                <div
                  key={theme.id}
                  className={
                    'flex items-center justify-between p-3 rounded-lg border transition-colors ' +
                    (theme.is_active
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50')
                  }
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Color preview */}
                    <div className="flex gap-0.5 flex-shrink-0">
                      {['primary', 'secondary', 'accent'].map((key) => (
                        <div
                          key={key}
                          className="w-4 h-4 rounded-sm border border-gray-200"
                          style={{
                            backgroundColor:
                              '#' + (theme.colors[key] || 'CCCCCC'),
                          }}
                        />
                      ))}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {theme.name}
                        {theme.is_active && (
                          <span className="ml-2 text-xs text-blue-600 font-normal">
                            (Active)
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400">
                        {theme.fonts.heading} / {theme.fonts.body}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!theme.is_active && (
                      <button
                        onClick={() => handleActivate(theme.id)}
                        className="text-xs text-blue-600 hover:text-blue-800 border border-blue-300 rounded px-2 py-1"
                        title="このテーマを使用"
                      >
                        <Palette size={12} />
                      </button>
                    )}
                    {deleteConfirm === theme.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(theme.id)}
                          className="text-xs text-red-600 hover:text-red-800 border border-red-300 rounded px-2 py-1"
                        >
                          OK
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded px-2 py-1"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(theme.id)}
                        className="text-xs text-gray-400 hover:text-red-500 border border-gray-200 rounded px-2 py-1"
                        title="削除"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesignUploadPage;
